import { Button, Modal, SearchBar } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { cms } from "../../api/cms.service";
import type { AeoPageFields, PageFieldsEntry, PageFieldsImportResult } from "../../types/cms";
import { PageHeader } from "../../components/PageHeader";
import LoadingState from "../../components/LoadingState";
import EmptyState from "../../components/EmptyState";
import { formatDateTimeShort } from "../../utils/dateFormatter";
import { usePermissions } from "../../hooks/usePermissions";
import styles from "./PageContentPage.module.css";
import cmsStyles from "./CMS.module.css";

const SCOPE_CLEARED_CODE = "SCOPE_CLEARED_NO_VALID_ROWS";

function asString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

type FooterLink = NonNullable<AeoPageFields["footer"]>[number];

function readFooterLinks(value: unknown): FooterLink[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const links: FooterLink[] = [];
  for (const item of value) {
    if (item && typeof item === "object") {
      const record = item as Record<string, unknown>;
      if (typeof record.label === "string" && typeof record.url === "string") {
        links.push({ label: record.label, url: record.url });
      }
    }
  }
  return links;
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={styles.fieldValue}>{value || "—"}</span>
    </div>
  );
}

function PageContentDetail({ entry, onClose }: { entry: PageFieldsEntry; onClose: () => void }) {
  const fields = entry.fields;
  const footerLinks = readFooterLinks(fields.footer);
  const liveUrl = `https://www.legal500.com${entry.urlPath}`;

  return (
    <Modal isOpen onClose={onClose} title={entry.urlPath} size="large">
      <div className={styles.detailPanel}>
        <div className={styles.detailMeta}>
          <FieldRow label="Publication" value={entry.publication} />
          <FieldRow label="BPA" value={entry.bpa} />
          <FieldRow label="URL path" value={entry.urlPath} />
          <FieldRow label="Updated" value={formatDateTimeShort(entry.updatedAt)} />
          <FieldRow label="Updated by" value={entry.updatedBy ?? "—"} />
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Live URL</span>
            <a className={styles.liveLink} href={liveUrl} target="_blank" rel="noopener noreferrer">
              {liveUrl}
            </a>
          </div>
        </div>

        <div className={styles.detailFields}>
          <FieldRow label="H1" value={asString(fields.h1)} />
          <FieldRow label="H2" value={asString(fields.h2)} />
          <FieldRow label="Body summary" value={asString(fields.body_summary)} />
          <FieldRow label="Body features" value={asString(fields.body_features)} />
        </div>

        <div className={styles.footerSection}>
          <span className={styles.fieldLabel}>Footer</span>
          {footerLinks.length === 0 ? (
            <span className={styles.fieldValue}>—</span>
          ) : (
            <ul className={styles.footerList}>
              {footerLinks.map(link => (
                <li key={`${link.label} ${link.url}`}>
                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className={styles.detailActions}>
          <Button variant="outlined" onClick={onClose} type="button">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

function IssuesTable({
  title,
  issues,
  variant,
}: {
  title: string;
  issues: PageFieldsImportResult["errors"];
  variant: "error" | "warning";
}) {
  const tableClass = variant === "error" ? styles.errorsTable : styles.warningsTable;

  return (
    <div className={styles.issuesSection}>
      <p className={styles.issuesTitle}>{title}</p>
      {issues.length === 0 ? (
        <p className={styles.issuesEmpty}>{variant === "error" ? "No errors" : "No warnings"}</p>
      ) : (
        <table className={tableClass}>
          <thead>
            <tr>
              <th>Row</th>
              <th>Code</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {issues.map(issue => (
              <tr key={`${issue.row} ${issue.code} ${issue.message}`}>
                <td>{issue.row === 0 ? "File" : issue.row}</td>
                <td>{issue.code}</td>
                <td>{issue.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function PageContentImportModal({ isOpen, onClose, onSuccess }: ImportModalProps) {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("cms.page_fields.write");

  const [file, setFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<PageFieldsImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  function handleClose() {
    setFile(null);
    setImportResult(null);
    setImportError(null);
    onClose();
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files?.[0] ?? null;
    setFile(selected);
  }

  async function handleSubmit() {
    if (!file) {
      setImportError("Please choose a .xlsx file");
      return;
    }
    setImportError(null);
    setImportResult(null);
    setIsSubmitting(true);
    try {
      const result = await cms.pageFieldsImport(file);
      setImportResult(result);
      onSuccess();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setIsSubmitting(false);
    }
  }

  const scopeCleared =
    importResult !== null &&
    (importResult.validRows === 0 ||
      importResult.warnings.some(warning => warning.code === SCOPE_CLEARED_CODE));
  const scopeClearedWarning = importResult?.warnings.find(
    warning => warning.code === SCOPE_CLEARED_CODE,
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Page Content" size="mediumLarge">
      <div className={styles.importForm}>
        <p className={styles.importHint}>
          Upload a .xlsx spreadsheet of ranking-page content. This replaces the content for each
          (publication, BPA) scope in the file.
        </p>

        <div className={styles.importField}>
          <input
            type="file"
            accept=".xlsx"
            className={styles.fileInput}
            onChange={handleFileChange}
          />
        </div>

        {importError && (
          <div className={styles.importError} role="alert">
            {importError}
          </div>
        )}

        {importResult && (
          <div className={styles.importResults} role="status">
            <p className={styles.importResultsTitle}>Import complete</p>
            <p className={styles.importSummary}>
              {importResult.validRows} of {importResult.totalRows} rows imported.
            </p>
          </div>
        )}

        {scopeCleared && (
          <div className={styles.scopeClearedBanner} role="alert">
            <p className={styles.scopeClearedTitle}>Scope cleared — nothing imported</p>
            <p>
              {scopeClearedWarning
                ? scopeClearedWarning.message
                : "No valid rows were found. The existing content for this publication and BPA scope was cleared and nothing was imported."}
            </p>
          </div>
        )}

        {importResult && (
          <>
            <IssuesTable title="Errors" issues={importResult.errors} variant="error" />
            <IssuesTable title="Warnings" issues={importResult.warnings} variant="warning" />
          </>
        )}

        <div className={styles.importActions}>
          <Button variant="outlined" onClick={handleClose} type="button">
            {importResult ? "Close" : "Cancel"}
          </Button>
          {!importResult && canWrite && (
            <Button color="primary" type="button" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Importing..." : "Import"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

function PageContentPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [entries, setEntries] = useState<PageFieldsEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [selectedUrlPath, setSelectedUrlPath] = useState<string | null>(null);
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("cms.page_fields.write");

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSearchChange(value: string) {
    setSearchTerm(value);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      const newParams = new URLSearchParams(searchParams);
      if (value) {
        newParams.set("search", value);
      } else {
        newParams.delete("search");
      }
      setSearchParams(newParams);
    }, 300);
  }

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const response = await cms.getPageFields({ urlPath: searchTerm || undefined });
      setEntries(response.entries);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Failed to load page content"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, [searchParams.get("search")]);

  useEffect(() => {
    setSearchTerm(searchParams.get("search") || "");
  }, [searchParams.get("search")]);

  const selectedEntry =
    selectedUrlPath !== null
      ? (entries.find(entry => entry.urlPath === selectedUrlPath) ?? null)
      : null;

  return (
    <div className={cmsStyles.wrapper}>
      <PageHeader
        title="Page Content"
        subtitle="AEO ranking-page content imported from spreadsheets"
      >
        {canWrite && (
          <Button variant="outlined" onClick={() => setImportModalOpen(true)}>
            Import
          </Button>
        )}
      </PageHeader>

      <PageContentImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={loadData}
      />

      {selectedEntry && (
        <PageContentDetail entry={selectedEntry} onClose={() => setSelectedUrlPath(null)} />
      )}

      <div className={cmsStyles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by URL path..."
          onClear={() => handleSearchChange("")}
        />
      </div>

      <div className={cmsStyles.contentSection}>
        {loading ? (
          <LoadingState message="Loading page content..." />
        ) : error ? (
          <EmptyState
            variant="error"
            title="Failed to load page content"
            message={error.message}
            action={{ label: "Retry", onClick: loadData }}
          />
        ) : entries.length === 0 ? (
          <EmptyState
            variant="empty"
            title="No page content"
            message="Import a spreadsheet to add ranking-page content."
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Publication</th>
                <th>BPA</th>
                <th>URL path</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(entry => (
                <tr
                  key={`${entry.publication} ${entry.bpa} ${entry.urlPath}`}
                  className={styles.row}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedUrlPath(entry.urlPath)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedUrlPath(entry.urlPath);
                    }
                  }}
                >
                  <td>{entry.publication}</td>
                  <td>{entry.bpa}</td>
                  <td className={styles.urlPathCell}>{entry.urlPath}</td>
                  <td>{formatDateTimeShort(entry.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default PageContentPage;
